import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import "moment/locale/pt-br";
import {
  HiArrowSmRight,
  HiCheck,
  HiOutlineBell,
  HiOutlineCheckCircle,
  HiOutlineClipboardCheck,
  HiOutlineDocumentText,
  HiOutlineExclamationCircle,
  HiOutlineEye,
  HiOutlineInformationCircle,
  HiX,
} from "react-icons/hi";

const TYPE_STYLES = {
  billing: {
    icon: HiOutlineDocumentText,
    iconBox: "bg-blue-50 text-blue-600 ring-1 ring-inset ring-blue-100",
    accent: "bg-blue-500",
    action: "text-blue-700 bg-blue-50 hover:bg-blue-100",
  },
  warning: {
    icon: HiOutlineExclamationCircle,
    iconBox: "bg-amber-50 text-amber-600 ring-1 ring-inset ring-amber-100",
    accent: "bg-amber-500",
    action: "text-amber-800 bg-amber-50 hover:bg-amber-100",
  },
  success: {
    icon: HiOutlineCheckCircle,
    iconBox: "bg-emerald-50 text-emerald-600 ring-1 ring-inset ring-emerald-100",
    accent: "bg-emerald-500",
    action: "text-emerald-800 bg-emerald-50 hover:bg-emerald-100",
  },
  monitoring: {
    icon: HiOutlineEye,
    iconBox: "bg-violet-50 text-violet-600 ring-1 ring-inset ring-violet-100",
    accent: "bg-violet-500",
    action: "text-violet-700 bg-violet-50 hover:bg-violet-100",
  },
  support: {
    icon: HiOutlineClipboardCheck,
    iconBox: "bg-cyan-50 text-cyan-700 ring-1 ring-inset ring-cyan-100",
    accent: "bg-cyan-500",
    action: "text-cyan-800 bg-cyan-50 hover:bg-cyan-100",
  },
  info: {
    icon: HiOutlineInformationCircle,
    iconBox: "bg-sky-50 text-sky-600 ring-1 ring-inset ring-sky-100",
    accent: "bg-sky-500",
    action: "text-sky-700 bg-sky-50 hover:bg-sky-100",
  },
  system: {
    icon: HiOutlineInformationCircle,
    iconBox: "bg-slate-100 text-slate-500 ring-1 ring-inset ring-slate-200",
    accent: "bg-slate-400",
    action: "text-slate-700 bg-slate-100 hover:bg-slate-200",
  },
};

const HIGH_PRIORITIES = ["HIGH", "URGENT", "ALTA", "URGENTE", "CRITICAL"];

const getNotificationDate = (notification) =>
  notification?.publishedAt || notification?.createdAt || null;

const getGroupLabel = (date) => {
  const value = moment(date);
  if (!date || !value.isValid()) return "Anteriores";
  if (value.isSame(moment(), "day")) return "Hoje";
  if (value.isSame(moment().subtract(1, "day"), "day")) return "Ontem";
  if (value.isAfter(moment().subtract(7, "days").startOf("day"))) {
    return "Últimos 7 dias";
  }
  return "Anteriores";
};

const GROUP_ORDER = ["Hoje", "Ontem", "Últimos 7 dias", "Anteriores"];

const formatRelativeTime = (date) => {
  const value = moment(date);
  if (!date || !value.isValid()) return "";
  if (moment().diff(value, "seconds") < 60) return "agora";
  if (moment().diff(value, "days") >= 7) return value.format("DD/MM/YYYY");
  return value.locale("pt-br").fromNow();
};

const formatFullDate = (date) => {
  const value = moment(date);
  if (!date || !value.isValid()) return "";
  return value.format("DD/MM/YYYY [às] HH:mm");
};

const NotificationCenter = ({
  notifications = [],
  unreadCount: serverUnreadCount = 0,
  isLoading = false,
  onMarkAsRead,
  onMarkAllAsRead,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [pendingReadIds, setPendingReadIds] = useState([]);
  const containerRef = useRef(null);

  useEffect(() => {
    setPendingReadIds((current) =>
      current.filter((id) => {
        const notification = notifications.find((item) => item.id === id);
        return notification && !notification.isRead;
      }),
    );
  }, [notifications]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    // Em tela cheia (mobile) trava o scroll do fundo, senão a página continua
    // rolando por trás do painel. No desktop é dropdown e não deve travar nada.
    const emTelaCheia = window.matchMedia("(max-width: 639px)").matches;
    const overflowAnterior = document.body.style.overflow;
    if (emTelaCheia) document.body.style.overflow = "hidden";

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
      if (emTelaCheia) document.body.style.overflow = overflowAnterior;
    };
  }, [isOpen]);

  const isUnread = (notification) =>
    !notification.isRead && !pendingReadIds.includes(notification.id);

  const visibleUnreadCount = useMemo(
    () => notifications.filter(isUnread).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [notifications, pendingReadIds],
  );
  const unreadCount = Math.max(
    0,
    Number(serverUnreadCount || 0) - pendingReadIds.length,
    visibleUnreadCount,
  );
  const counterLabel = unreadCount > 9 ? "9+" : unreadCount;

  const filteredNotifications = useMemo(
    () =>
      activeTab === "unread" ? notifications.filter(isUnread) : notifications,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [notifications, activeTab, pendingReadIds],
  );

  const groupedNotifications = useMemo(() => {
    const groups = new Map();
    filteredNotifications.forEach((notification) => {
      const label = getGroupLabel(getNotificationDate(notification));
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label).push(notification);
    });
    return GROUP_ORDER.filter((label) => groups.has(label)).map((label) => ({
      label,
      items: groups.get(label),
    }));
  }, [filteredNotifications]);

  const markAsRead = async (id) => {
    const notification = notifications.find((item) => item.id === id);
    if (notification?.isRead || pendingReadIds.includes(id)) return;

    setPendingReadIds((current) => [...current, id]);
    try {
      await onMarkAsRead?.(id);
    } catch {
      setPendingReadIds((current) => current.filter((item) => item !== id));
    }
  };

  const markAllAsRead = async () => {
    const unreadIds = notifications
      .filter(({ isRead }) => !isRead)
      .map(({ id }) => id);
    setPendingReadIds((current) =>
      Array.from(new Set([...current, ...unreadIds])),
    );
    try {
      await onMarkAllAsRead?.();
    } catch {
      setPendingReadIds([]);
    }
  };

  const renderNotification = (notification) => {
    const unread = isUnread(notification);
    // O backend grava o tipo em maiúsculas (SUCCESS, BILLING, WARNING...) e as
    // chaves aqui são minúsculas: sem normalizar, tudo caía no estilo genérico.
    const styles =
      TYPE_STYLES[String(notification.type || "").toLowerCase()] ||
      TYPE_STYLES.system;
    const NotificationIcon = styles.icon;
    const date = getNotificationDate(notification);
    const relativeTime = formatRelativeTime(date);
    const isImportant = HIGH_PRIORITIES.includes(
      String(notification.priority || "").toUpperCase(),
    );

    const content = (
      <>
        {unread && (
          <span
            className={`absolute left-0 top-3 bottom-3 w-1 rounded-r-full ${styles.accent}`}
          />
        )}
        <span
          className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-transform duration-200 group-hover:scale-105 ${styles.iconBox}`}
        >
          <NotificationIcon className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-start justify-between gap-2">
            <strong
              className={`text-sm leading-snug ${
                unread
                  ? "font-semibold text-slate-900"
                  : "font-medium text-slate-600"
              }`}
            >
              {notification.title}
              {isImportant && (
                <span className="ml-2 inline-flex -translate-y-px items-center rounded-full bg-red-50 px-1.5 py-0.5 align-middle text-[10px] font-bold uppercase tracking-wide text-red-600 ring-1 ring-inset ring-red-100">
                  Importante
                </span>
              )}
            </strong>
            <span className="flex shrink-0 items-center gap-1.5 pt-0.5">
              {relativeTime && (
                <time
                  title={formatFullDate(date)}
                  className="whitespace-nowrap text-[11px] font-medium text-slate-400"
                >
                  {relativeTime}
                </time>
              )}
              {unread && (
                <span className="h-2 w-2 shrink-0 rounded-full bg-blue-600" />
              )}
            </span>
          </span>
          <span
            className={`mt-1 block text-xs leading-5 line-clamp-2 ${
              unread ? "text-slate-600" : "text-slate-400"
            }`}
          >
            {notification.description}
          </span>
          {(notification.label || unread) && (
            <span className="mt-2 flex items-center justify-between gap-2">
            {notification.label ? (
              <span
                className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold transition ${styles.action}`}
              >
                {notification.label}
                <HiArrowSmRight className="h-3.5 w-3.5" />
              </span>
            ) : (
              <span />
            )}
            {unread && (
              <span
                role="button"
                tabIndex={-1}
                title="Marcar como lida"
                aria-label="Marcar como lida"
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  markAsRead(notification.id);
                }}
                className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-300 opacity-0 transition hover:bg-blue-50 hover:text-blue-600 focus:opacity-100 group-hover:opacity-100"
              >
                <HiCheck className="h-4 w-4" />
              </span>
            )}
            </span>
          )}
        </span>
      </>
    );

    const className = `group relative flex w-full gap-3 px-5 py-3.5 text-left transition-colors duration-150 hover:bg-slate-50 ${
      unread ? "bg-blue-50/30" : "bg-white"
    }`;

    return notification.link ? (
      <Link
        key={notification.id}
        to={notification.link}
        onClick={() => {
          markAsRead(notification.id);
          setIsOpen(false);
        }}
        className={className}
      >
        {content}
      </Link>
    ) : (
      <button
        key={notification.id}
        type="button"
        onClick={() => markAsRead(notification.id)}
        className={className}
      >
        {content}
      </button>
    );
  };

  return (
    <div ref={containerRef} className="relative">
      <style>{`
        @keyframes nc-panel-in {
          from { opacity: 0; transform: translateY(-6px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        aria-label={
          unreadCount
            ? `Notificações: ${unreadCount} não lidas`
            : "Notificações"
        }
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        className={`relative flex h-10 w-10 items-center justify-center rounded-xl border text-white/65 transition hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-400/60 ${
          isOpen
            ? "border-white/25 bg-white/[0.12] text-white"
            : "border-white/10 bg-white/[0.04] hover:border-white/20 hover:bg-white/[0.09]"
        }`}
      >
        <HiOutlineBell className="h-5 w-5" />
        {unreadCount > 0 && (
          <>
            <span className="absolute -right-1 -top-1 h-5 min-w-5 animate-ping rounded-full bg-red-500/50 [animation-duration:2.4s]" />
            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-[#0b2542] bg-red-500 px-1 text-[10px] font-bold leading-none text-white">
              {counterLabel}
            </span>
          </>
        )}
      </button>

      {/*
        No mobile o painel ocupa a tela inteira: ancorado ao sino ele ficava
        espremido e difícil de ler. A partir de sm volta a ser o dropdown de
        sempre. Como em tela cheia não existe "clicar fora", o cabeçalho ganha
        um botão de fechar visível apenas no mobile.
      */}
      {isOpen && (
        <section
          role="dialog"
          aria-label="Central de notificações"
          style={{ animation: "nc-panel-in 0.18s ease-out" }}
          className="fixed inset-0 z-[100] flex flex-col border-slate-200 bg-white pt-[env(safe-area-inset-top)] text-left sm:absolute sm:inset-auto sm:right-0 sm:top-12 sm:block sm:w-[420px] sm:overflow-hidden sm:rounded-2xl sm:border sm:pt-0 sm:shadow-[0_24px_70px_rgba(15,23,42,0.28)]"
        >
          <header className="flex shrink-0 items-start justify-between gap-3 px-5 pb-3 pt-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                  Notificações
                </h2>
                {unreadCount > 0 && (
                  <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-blue-600 px-1.5 text-[10px] font-bold leading-none text-white">
                    {counterLabel}
                  </span>
                )}
              </div>
              <p className="mt-0.5 text-xs text-slate-500">
                {unreadCount
                  ? `${unreadCount} ${unreadCount === 1 ? "nova atualização" : "novas atualizações"}`
                  : "Você está em dia"}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={markAllAsRead}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-blue-700 transition hover:bg-blue-50"
                >
                  <HiCheck className="h-4 w-4" />
                  Marcar todas
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label="Fechar notificações"
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 sm:hidden"
              >
                <HiX className="h-5 w-5" />
              </button>
            </div>
          </header>

          <div className="mx-5 mb-2 grid shrink-0 grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
            {[
              { key: "all", label: "Todas" },
              { key: "unread", label: "Não lidas" },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                aria-pressed={activeTab === tab.key}
                className={`flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  activeTab === tab.key
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {tab.label}
                {tab.key === "unread" && unreadCount > 0 && (
                  <span
                    className={`inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none ${
                      activeTab === "unread"
                        ? "bg-blue-600 text-white"
                        : "bg-slate-300 text-slate-700"
                    }`}
                  >
                    {counterLabel}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto overscroll-contain border-t border-slate-100 pb-[env(safe-area-inset-bottom)] sm:max-h-[min(480px,70vh)] sm:flex-none sm:pb-0">
            {isLoading ? (
              <div
                className="space-y-4 p-5"
                aria-label="Carregando notificações"
              >
                {[0, 1, 2, 3].map((item) => (
                  <div key={item} className="flex animate-pulse gap-3">
                    <span className="h-10 w-10 shrink-0 rounded-xl bg-slate-200" />
                    <span className="flex-1 space-y-2 py-1">
                      <span className="flex items-center justify-between">
                        <span className="block h-3 w-2/5 rounded bg-slate-200" />
                        <span className="block h-2.5 w-12 rounded bg-slate-100" />
                      </span>
                      <span className="block h-2.5 w-full rounded bg-slate-100" />
                      <span className="block h-2.5 w-3/4 rounded bg-slate-100" />
                    </span>
                  </div>
                ))}
              </div>
            ) : groupedNotifications.length ? (
              <>
                {groupedNotifications.map((group) => (
                  <div key={group.label}>
                    <p className="sticky top-0 z-10 border-b border-slate-100 bg-slate-50/95 px-5 py-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400 backdrop-blur">
                      {group.label}
                    </p>
                    <div className="divide-y divide-slate-100">
                      {group.items.map(renderNotification)}
                    </div>
                  </div>
                ))}
                {notifications.length >= 50 && (
                  <p className="border-t border-slate-100 bg-slate-50 px-5 py-2.5 text-center text-[11px] text-slate-400">
                    Mostrando as 50 notificações mais recentes
                  </p>
                )}
              </>
            ) : activeTab === "unread" ? (
              <div className="flex flex-col items-center px-8 py-12 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-500 ring-1 ring-inset ring-emerald-100">
                  <HiOutlineCheckCircle className="h-7 w-7" />
                </span>
                <h3 className="mt-4 text-sm font-bold text-slate-800">
                  Tudo lido por aqui
                </h3>
                <p className="mt-1 max-w-xs text-xs leading-5 text-slate-500">
                  Você não tem notificações pendentes. Novas atualizações
                  aparecerão nesta aba.
                </p>
                {notifications.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setActiveTab("all")}
                    className="mt-4 rounded-lg px-3 py-1.5 text-xs font-semibold text-blue-700 transition hover:bg-blue-50"
                  >
                    Ver todas as notificações
                  </button>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center px-8 py-12 text-center">
                <span className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                  <HiOutlineBell className="h-7 w-7" />
                </span>
                <h3 className="mt-4 text-sm font-bold text-slate-800">
                  Nenhuma notificação
                </h3>
                <p className="mt-1 max-w-xs text-xs leading-5 text-slate-500">
                  Novidades, alertas e atualizações importantes do sistema
                  aparecerão aqui.
                </p>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default NotificationCenter;
